import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import TopNavigation from '../components/TopNavigation';
import LeftNavSidebar from '../components/LeftNavSidebar';
import { auth } from '../firebase';
import VerifiedBadge from '../components/VerifiedBadge';
import ImageWithFallback from '../components/ImageWithFallback';
import MobileContainer from '../components/MobileContainer';
import Card from '../components/Card';
import { listNotifications, markNotificationRead } from '../utils/secureFunctions';

interface Notification {
  id: string;
  type: 'like' | 'comment' | 'follow' | 'mention' | 'system';
  fromUserId: string;
  fromUserName: string;
  fromUserPhoto: string;
  fromUserVerified?: boolean;
  message: string;
  snippet?: string;
  contentId?: string | null;
  contentType?: 'post' | 'pulse' | 'testimony' | 'link' | null;
  createdAt: string;
  read: boolean;
}

const Notifications: React.FC = () => {
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();

  useEffect(() => {
    loadNotifications();
  }, []);

  const loadNotifications = async () => {
    if (!auth.currentUser) return;

  setLoading(true);
  setError(null);
    try {
      const res = await listNotifications({ limit: 50 });
      setNotifications(res.notifications as Notification[]);
    } catch (error: any) {
      console.error('Error loading notifications:', error);
      const code = error?.code || '';
      if (code === 'permission-denied') {
        setError('Notifications are unavailable due to permissions. Please sign in and retry.');
      } else {
        setError('Failed to load notifications. Please try again later.');
      }
    } finally {
      setLoading(false);
    }
  };

  const [isMobile, setIsMobile] = useState<boolean>(() => {
    try { return window.innerWidth <= 768; } catch { return false; }
  });
  useEffect(() => {
    const onResize = () => { try { setIsMobile(window.innerWidth <= 768); } catch {} };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  return (
    <div style={styles.container}>
      <TopNavigation />
      {!isMobile && <LeftNavSidebar />}
      <div style={{ marginLeft: isMobile ? 0 : styles.main.marginLeft }}>
        <MobileContainer maxWidth={800}>
        <div style={styles.header}>
          <h1 style={styles.title}>🔔 Notifications</h1>
          <p style={styles.subtitle}>Stay updated with your latest activity</p>
        </div>

        {loading ? null : error ? (
          <div style={styles.emptyState}>
            <h3 style={{ margin: 0, marginBottom: 8, color: '#072B2C' }}>Cannot display notifications</h3>
            <p style={{ margin: 0, color: '#5B6E6E' }}>{error}</p>
          </div>
        ) : notifications.length === 0 ? (
          <Card style={styles.emptyState as any}>
            <div style={{ fontSize: 64, marginBottom: 16 }}>🔔</div>
            <h3 style={{ margin: 0, marginBottom: 8, color: '#072B2C' }}>No notifications yet</h3>
            <p style={{ margin: 0, color: '#5B6E6E' }}>When you get notifications, they'll show up here</p>
          </Card>
        ) : (
          <div style={styles.notificationsList}>
            {notifications.map((notif) => (
              <Card
                key={notif.id}
                style={{ ...styles.notificationItem, backgroundColor: notif.read ? '#FFFFFF' : '#F0F9FA' }}
                onClick={async () => {
                  // Navigate based on content type
                  try {
                    if (notif.contentType === 'link' && notif.contentId && notif.contentId.startsWith('/')) {
                      navigate(notif.contentId);
                    } else if (notif.contentType && notif.contentId) {
                      if (notif.contentType === 'pulse') {
                        navigate(`/pulse?videoId=${encodeURIComponent(notif.contentId)}`);
                      } else if (notif.contentType === 'post') {
                        navigate(`/post/${encodeURIComponent(notif.contentId)}`);
                      } else if (notif.contentType === 'testimony') {
                        navigate(`/testimony?id=${encodeURIComponent(notif.contentId)}`);
                      }
                    }
                    // Mark as read (best-effort)
                    if (!notif.read) {
                      try {
                        await markNotificationRead(notif.id, true);
                        setNotifications(prev => prev.map(n => (n.id === notif.id ? { ...n, read: true } : n)));
                      } catch {}
                    }
                  } catch (e) {
                    console.warn('Notification navigation failed', e);
                  }
                }}
              >
                <ImageWithFallback
                  src={notif.fromUserPhoto}
                  alt={notif.fromUserName}
                  style={styles.avatar}
                />
                <div style={styles.notificationContent}>
                  <p style={styles.notificationText}>
                    <strong>{notif.fromUserName}{notif.fromUserVerified ? <VerifiedBadge size={14} /> : null}</strong> {notif.message}
                  </p>
                  {notif.snippet && (
                    <p style={styles.notificationSnippet}>
                      “{notif.snippet}”
                    </p>
                  )}
                  <p style={styles.notificationTime}>
                    {notif.createdAt ? new Date(notif.createdAt).toLocaleString() : 'Just now'}
                  </p>
                </div>
              </Card>
            ))}
          </div>
        )}
        </MobileContainer>
      </div>
    </div>
  );
};

const styles: { [key: string]: React.CSSProperties } = {
  container: {
    minHeight: '100vh',
    backgroundColor: '#F5EDE1',
  },
  main: {
    marginLeft: '280px',
    marginTop: '60px',
    padding: '24px',
    maxWidth: '800px',
  },
  header: {
    marginBottom: '24px',
  },
  title: {
    margin: 0,
    marginBottom: 4,
    fontSize: 32,
    color: '#072B2C',
  },
  subtitle: {
    margin: 0,
    color: '#5B6E6E',
    fontSize: 14,
  },
  emptyState: {
    textAlign: 'center',
    padding: '60px 20px',
    backgroundColor: '#FFFFFF',
    borderRadius: '12px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
  },
  notificationsList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
  },
  notificationItem: {
    display: 'flex',
    gap: '16px',
    padding: '16px',
    backgroundColor: '#FFFFFF',
    borderRadius: '12px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
    transition: 'transform 0.2s',
    cursor: 'pointer',
  },
  avatar: {
    width: '48px',
    height: '48px',
    borderRadius: '50%',
    objectFit: 'cover' as const,
  },
  notificationContent: {
    flex: 1,
  },
  notificationText: {
    margin: 0,
    marginBottom: 4,
    fontSize: 14,
    color: '#072B2C',
  },
  notificationTime: {
    margin: 0,
    fontSize: 12,
    color: '#5B6E6E',
  },
  notificationSnippet: {
    margin: 0,
    marginBottom: 4,
    fontSize: 13,
    color: '#5B6E6E',
  },
};

export default Notifications;



